import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import React from 'react';
import PropTypes from 'prop-types';
import ThirdPartySelect from 'react-select';
import styled from 'styled-components';
import { DropdownIndicator } from './subcomponents/DropdownIndicator';
import { SingleValue } from './subcomponents/SingleValue';
import { Menu } from './subcomponents/Menu';
import { MenuList } from './subcomponents/MenuList';
import ValidationIcon from '../icons/ValidationIcon';
import { SCALE, CONTAINER_SIZES, FONT_SIZES, RADIUS_SIZES, LINE_HEIGHT_SIZES, VALIDATION_STATES } from '../../utils/constants';
import { alto, apple, black, bondiBlue, boulder, catskillWhite, fairPink, frenchPass, java, mercury, thunderBird, white, willowBrook, GridContainerContext } from '@parchment/atoms';
import Skeleton from 'react-loading-skeleton';
export class SelectFieldDeprecated extends React.Component {
  static get propTypes() {
    return {
      ...ThirdPartySelect.propTypes,
      placeholder: PropTypes.node,
      scale: PropTypes.oneOf(Object.values(SCALE)),
      minWidth: PropTypes.string,
      required: PropTypes.bool,
      isDisabled: PropTypes.bool,
      showRequiredInPlaceholder: PropTypes.bool,
      deselectText: PropTypes.node,
      disableDeselect: PropTypes.bool,
      disableBorder: PropTypes.bool,
      valid: PropTypes.oneOf(Object.values(VALIDATION_STATES)),
      value: PropTypes.object,
      select: PropTypes.object,
      onChange: PropTypes.func.isRequired,
      onBlur: PropTypes.func,
      onFocus: PropTypes.func,
      visited: PropTypes.bool,
      loading: PropTypes.bool
    };
  }
  static get defaultProps() {
    return {
      scale: SCALE.medium,
      valid: VALIDATION_STATES.neutral,
      minWidth: '150px',
      maxMenuHeight: 250,
      isDisabled: false,
      showRequiredInPlaceholder: true,
      deselectText: 'Clear Selection',
      disableDeselect: true,
      disableBorder: false,
      select: /*#__PURE__*/React.createRef(),
      visited: false,
      required: false,
      loading: false
    };
  }
  constructor(props) {
    super(props);
    _defineProperty(this, "select", /*#__PURE__*/React.createRef());
    _defineProperty(this, "combinedRef", ref => {
      window.r = ref;
      this.select.current = ref;
      if (typeof this.props.select === 'function') {
        this.props.select(ref);
      } else if (typeof this.props.select === 'object') {
        this.props.select.current = ref;
      }
    });
    _defineProperty(this, "autofillHack", () => {
      if (!this.select.current) {
        return;
      }
      const input = this.select.current.querySelector('input[type="text"]');
      if (!input) {
        return;
      }
      if (this.autofillListener) {
        input.removeEventListener('change', this.autofillListener);
      }
      this.autofillListener = e => {
        var _this$props$value;
        const firstMatch = this.props.options.find(option => {
          var _e$target;
          return option.value === ((_e$target = e.target) === null || _e$target === void 0 ? void 0 : _e$target.value);
        });
        if (firstMatch && (firstMatch === null || firstMatch === void 0 ? void 0 : firstMatch.value) !== ((_this$props$value = this.props.value) === null || _this$props$value === void 0 ? void 0 : _this$props$value.value)) {
          this.handleChange(firstMatch);
        }
      };
      input.addEventListener('change', this.autofillListener);
    });
    this.getValidationIcon = this.getValidationIcon.bind(this);
    this.getPlaceholder = this.getPlaceholder.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.select = /*#__PURE__*/React.createRef();
    this.state = {
      visited: props.visited
    };
  }
  getValidationIcon() {
    let {
      visited
    } = this.state;
    let {
      isDisabled,
      required,
      showRequiredInPlaceholder,
      valid,
      value
    } = this.props;
    return /*#__PURE__*/React.createElement(ValidationIcon, {
      valid: valid,
      disabled: isDisabled,
      required: showRequiredInPlaceholder && required,
      visited: visited,
      value: value
    });
  }
  handleBlur(e) {
    e.persist();
    if (!this.state.visited) {
      this.setState({
        visited: true
      });
    }
    this.props.onBlur && this.props.onBlur(e, this.props.value);
  }
  handleChange(newValue) {
    let value;
    if (newValue === this.props.value && !this.props.disableDeselect || newValue === null) {
      value = null;
    } else {
      value = newValue;
    }
    this.props.onChange && this.props.onChange(value);
  }
  handleFocus(e) {
    this.props.onFocus && this.props.onFocus(e);
  }
  getPlaceholder() {
    return /*#__PURE__*/React.createElement("span", null, this.getValidationIcon(), this.props.title || this.props.placeholder);
  }
  componentDidMount() {
    var _this$select$current;
    this.autofillHack();
    const ariaLabel = `select from ${this.props.name || 'the following choices'}`;
    (_this$select$current = this.select.current) === null || _this$select$current === void 0 ? void 0 : _this$select$current.querySelector('input[type=text]').setAttribute('aria-label', ariaLabel);
  }

  /**
   * A hack to support autofill. Though it depends on the actual "text" input
   * field that react-select creates by default. Essentially chrome tries to
   * fill in that field and react-select clears it out. So we add our listener
   * for the change event and then see if the value matches and option in the
   * list. If it does, we will trigger our `handleChange` function. So
   * technically this also depends on the component being used in a
   * controlled fashion (i.e. where some parent component listens for changes
   * and then updates the value prop (which is the most common use case).
   * Unfortunately there doesn't appear to be much traction in getting
   * better support baked in yet.
   * https://github.com/JedWatson/react-select/pull/2395
   */

  render() {
    const placeholder = this.getPlaceholder();
    if (this.props.loading) {
      return /*#__PURE__*/React.createElement("div", {
        style: {
          lineHeight: 0
        }
      }, /*#__PURE__*/React.createElement(Skeleton, {
        height: CONTAINER_SIZES[this.props.scale]
      }));
    }
    return /*#__PURE__*/React.createElement(GridContainerContext.Consumer, null, _ref => {
      let {
        edges
      } = _ref;
      return /*#__PURE__*/React.createElement(StyledSelect, {
        className: this.props.className,
        isDisabled: this.props.isDisabled,
        ref: this.combinedRef
      }, /*#__PURE__*/React.createElement(ThirdPartySelect, _extends({}, this.props, {
        role: "listbox",
        edges: edges,
        onBlur: this.handleBlur,
        onChange: this.handleChange,
        onFocus: this.handleFocus,
        value: this.props.value,
        placeholder: placeholder,
        styles: customStyles,
        components: {
          DropdownIndicator,
          SingleValue,
          Menu,
          MenuList
        },
        valid: this.props.valid,
        visited: this.state.visited
      })));
    });
  }
}
const StyledSelect = /*#__PURE__*/styled.div.withConfig({
  displayName: "SelectFieldDeprecated__StyledSelect",
  componentId: "sc-1kchi48-0"
})(["&:hover{cursor:", ";}input{font-size:", ";line-height:", ";min-height:", ";max-height:", ";{"], props => props.isDisabled ? 'not-allowed' : 'default', props => FONT_SIZES[props.scale], props => LINE_HEIGHT_SIZES[props.scale], props => LINE_HEIGHT_SIZES[props.scale], props => LINE_HEIGHT_SIZES[props.scale]);
StyledSelect.displayName = 'StyledSelect';
const customStyles = {
  container: (base, _ref2) => {
    let {
      selectProps
    } = _ref2;
    return {
      ...base,
      minWidth: selectProps.minWidth,
      borderRadius: 'inherit'
    };
  },
  control: (base, _ref3) => {
    let {
      isFocused,
      isDisabled,
      selectProps
    } = _ref3;
    let {
      valid,
      disableBorder,
      visited,
      value,
      edges
    } = selectProps;
    let backgroundColor = white;
    let zIndex = 0;
    let boxShadow = getBoxShadow(valid, visited, value, isFocused, disableBorder, isDisabled);
    if (valid !== VALIDATION_STATES.neutral && (visited || value)) {
      backgroundColor = valid === VALIDATION_STATES.success ? willowBrook : fairPink;
    } else if (isFocused) {
      backgroundColor = catskillWhite;
    }
    if (isFocused) {
      zIndex = 1;
    }
    if (isDisabled) {
      backgroundColor = mercury;
    }
    const gridCellProps = {};
    if (!edges || edges.length > 0) {
      gridCellProps.marginTop = '-1px';
      gridCellProps.marginLeft = '-1px';
      gridCellProps.borderRadius = 0;
      const radius = RADIUS_SIZES[selectProps.scale];
      if (edges.indexOf('top') >= 0 && edges.indexOf('left') >= 0) {
        gridCellProps.borderTopLeftRadius = radius;
      }
      if (edges.indexOf('top') >= 0 && edges.indexOf('right') >= 0) {
        gridCellProps.borderTopRightRadius = radius;
      }
      if (edges.indexOf('bottom') >= 0 && edges.indexOf('right') >= 0) {
        gridCellProps.borderBottomRightRadius = radius;
      }
      if (edges.indexOf('bottom') >= 0 && edges.indexOf('left') >= 0) {
        gridCellProps.borderBottomLeftRadius = radius;
      }
    }
    return {
      ...base,
      borderRadius: disableBorder ? 'inherit' : RADIUS_SIZES[selectProps.scale],
      borderColor: alto,
      borderWidth: 0,
      boxShadow,
      backgroundColor,
      zIndex,
      ...gridCellProps
    };
  },
  dropdownIndicator: base => ({
    ...base,
    marginTop: -4
  }),
  group: base => ({
    ...base,
    padding: 0
  }),
  groupHeading: base => ({
    ...base,
    minHeight: 1,
    lineHeight: '30px',
    boxShadow: `inset 0 1px 0 0 ${alto}, inset 0 -1px 0 0 ${alto}`
  }),
  indicatorSeparator: base => ({
    ...base,
    display: 'none'
  }),
  menu: (base, _ref4) => {
    let {
      selectProps
    } = _ref4;
    return {
      ...base,
      marginTop: 0,
      minWidth: 100,
      backgroundColor: 'transparent',
      boxShadow: 'none',
      transform: `translateX(-${(base.width - selectProps.menuOffsetX) / 2})`
    };
  },
  menuList: (base, _ref5) => {
    let {
      selectProps
    } = _ref5;
    return {
      ...base,
      borderRadius: RADIUS_SIZES[selectProps.scale],
      fontSize: FONT_SIZES[selectProps.scale],
      lineHeight: LINE_HEIGHT_SIZES[selectProps.scale],
      backgroundColor: white,
      boxShadow: '0 0 0 1px hsla(0,0%,0%,0.1), 0 4px 11px hsla(0,0%,0%,0.1)'
    };
  },
  option: (base, _ref6) => {
    let {
      isDisabled,
      isFocused,
      isSelected,
      selectProps
    } = _ref6;
    let backgroundColor = 'transparent';
    let color = black;
    let boxShadow = '';
    let fontStyle = 'normal';
    if (isDisabled) {
      backgroundColor = isFocused ? mercury : white;
      color = isFocused ? boulder : alto;
      fontStyle = 'italic';
    } else if (isFocused) {
      backgroundColor = java;
      color = white;
    } else if (isSelected) {
      backgroundColor = catskillWhite;
      boxShadow = `inset 50px 0 0 -44px ${frenchPass}`;
    }
    return {
      ...base,
      backgroundColor,
      color,
      boxShadow,
      fontStyle,
      fontSize: FONT_SIZES[selectProps.scale],
      padding: '5px 15px 5px 15px',
      ':active': {
        ...base[':active'],
        backgroundColor,
        color
      },
      ':hover': {
        ...base[':hover'],
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        backgroundColor: isDisabled ? mercury : java,
        color: isDisabled ? boulder : white,
        boxShadow: 'none'
      }
    };
  },
  placeholder: (base, _ref7) => {
    let {
      selectProps,
      isFocused,
      isDisabled
    } = _ref7;
    const {
      scale,
      valid
    } = selectProps;
    return {
      ...base,
      fontSize: FONT_SIZES[scale],
      lineHeight: LINE_HEIGHT_SIZES[scale],
      maxHeight: LINE_HEIGHT_SIZES[scale],
      color: valid === VALIDATION_STATES.success && !isDisabled ? apple : boulder,
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      visibility: isFocused ? 'hidden' : 'visible'
    };
  },
  singleValue: (base, _ref8) => {
    let {
      selectProps,
      isFocused,
      isDisabled
    } = _ref8;
    const {
      scale,
      valid
    } = selectProps;
    const color = isDisabled ? boulder : black;
    return {
      ...base,
      fontSize: FONT_SIZES[scale],
      fontWeight: 600,
      lineHeight: LINE_HEIGHT_SIZES[scale],
      maxHeight: LINE_HEIGHT_SIZES[scale],
      color: valid === VALIDATION_STATES.success || valid === VALIDATION_STATES.neutral ? color : thunderBird,
      overflow: 'hidden',
      whiteSpace: 'nowrap',
      visibility: isFocused ? 'hidden' : 'visible'
    };
  },
  valueContainer: (base, _ref9) => {
    let {
      selectProps
    } = _ref9;
    return {
      ...base,
      minHeight: CONTAINER_SIZES[selectProps.scale],
      maxHeight: CONTAINER_SIZES[selectProps.scale],
      paddingLeft: 12
    };
  }
};
const getBoxShadow = (valid, visited, value, isFocused, disableBorder, disabled) => {
  let validationInset = '',
    focusShadow = '',
    defaultBorder = '';
  if (valid !== VALIDATION_STATES.neutral) {
    let validationColor = valid === VALIDATION_STATES.success ? apple : thunderBird;
    if (visited || value) {
      validationInset = `inset 50px 0 0 -43px ${validationColor}`;
    }
    if (isFocused) {
      focusShadow = `0 0 5px 0 ${validationColor}`;
    }
  } else if (isFocused) {
    focusShadow = `0 0 5px 0 ${bondiBlue}`;
  }
  if (disabled) {
    validationInset = `inset 50px 0 0 -43px ${alto}`;
  }
  defaultBorder = disableBorder ? '' : `inset 0 0 0 1px ${alto}`;
  return [validationInset, focusShadow, defaultBorder].filter(shadow => shadow.trim().length > 0).join(', ');
};